<script lang="ts">
  import {
    BUTTON_VARIANT,
    BUTTON_SIZE,
    OPT_IN_MARKETING_COUNTRIES,
  } from '$lib/constants/constants';
  import Button from '$lib/components/control/Button.svelte';
  import TextInput from '$lib/components/paywall/TextInput.svelte';
  import Checkbox from '$lib/components/control/Checkbox.svelte';
  import { applyAction, enhance } from '$app/forms';
  import { setUser } from '$lib/stores/userStore';
  import { login } from '$lib/gateway/auth0Client';
  import { getMe } from '$lib/helpers/auth0Helpers';
  import Cookies from 'js-cookie';
  import { setAccessToken, clearStripeCookies } from '$lib/helpers/cookieHelpers';
  import { initBraze } from '$lib/gateway/brazeClient';
  import { createAccount } from '$lib/gateway/plausible';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export let form;
  export let onCompleted: (user) => void;
  export let onLogin: () => void;
  export let country: string | undefined = undefined;
  let loading = false;
  let acceptTerms = false;

  $: isOptInMarketingCountry = OPT_IN_MARKETING_COUNTRIES.find(
    (optInCountry) => optInCountry === country,
  );
  $: optInMarketing = isOptInMarketingCountry ? true : false;
  $: emailTaken = form?.errors?.message === 'The user already exists.';
</script>

<h2 class="text-md md:text-xl font-bold mb-6">Create an account</h2>

<form
  method="POST"
  action="?/signup"
  use:enhance={({ formData }) => {
    loading = true;
    formData.append('optInMarketing', optInMarketing.toString());
    formData.set('email', formData?.get('email')?.toString()?.toLowerCase() || '');

    return async ({ result, update }) => {
      if (result.type === 'success') {
        const user = result.data?.user;
        let unknownError = false;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (user) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const res = await login(
            formData.get('email'),
            formData.get('password'),
            `${location.origin}/api/auth/callback`,
          );
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (res.statusCode) {
            unknownError = true;
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await setAccessToken(Cookies, res);
            const userInfo = await getMe();
            if (userInfo) {
              setUser(userInfo);

              (async () => {
                const braze = await initBraze();

                if (braze) {
                  braze.changeUser(userInfo.user_metadata.user_canonical_id);
                }
              })();

              onCompleted(userInfo);
            } else {
              unknownError = true;
            }
          }
        } else {
          unknownError = true;
        }

        if (unknownError) {
          alert('An error occurred. Please refresh the page and try again.');
          clearStripeCookies(Cookies);
        }
      } else {
        await applyAction(result);
      }
      loading = false;
      update();
    };
  }}
  class="flex flex-col gap-1"
  id="signup"
>
  <TextInput
    name="firstName"
    label="First Name"
    value={form?.firstName ?? ''}
    error={form?.errors?.firstName}
    plausibleClass="firstName"
  />
  <TextInput
    name="lastName"
    label="Last Name"
    value={form?.lastName ?? ''}
    error={form?.errors?.lastName}
    plausibleClass="lastName"
  />
  <TextInput
    name="dob"
    type="date"
    label="Date of Birth"
    value={form?.dob ?? ''}
    error={form?.errors?.dob}
    max="3000-01-01"
    onfocus="this.max=new Date().toISOString().split('T')[0]"
    plausibleClass="dob"
  />

  <TextInput
    name="email"
    label="Email"
    value={form?.email ?? ''}
    error={form?.errors?.email || emailTaken}
    plausibleClass="email"
  >
    {#if emailTaken && form?.email}
      <div class="flex flex-col items-start gap-4 p-4 mt-1 bg-error-100 text-black rounded-sm">
        <p class="mt-1">
          The email <span class="underline">{form?.email}</span> is used by an existing account, if that’s
          your account login or use a different email.
        </p>
        <Button
          text="Login"
          variant={BUTTON_VARIANT.OUTLINED}
          buttonSize={BUTTON_SIZE.CUSTOM}
          customClass="px-6 py-2.5"
          onClick={onLogin}
        />
      </div>
    {/if}
  </TextInput>

  <TextInput
    name="password"
    type="password"
    label="Password"
    value={form?.password ?? ''}
    error={form?.errors?.password}
    plausibleClass="password"
  />

  <span class="text-md text-sweat-gray-50 mt-4"> Password requirements: </span>
  <ul class="text-md text-sweat-gray-50 list-disc ml-4">
    <li>Minimum 8 characters</li>
  </ul>

  <div class="my-4">
    {#if !isOptInMarketingCountry}
      <Checkbox
        plausibleClass="newsletters"
        theme="black"
        size="lg"
        label="Yes, I want to receive news, updates and discounts from Sweat."
        bind:checked={optInMarketing}
      />
    {/if}
    <Checkbox
      dataTestId="accept-terms"
      plausibleClass="terms"
      theme="black"
      size="lg"
      bind:checked={acceptTerms}
    >
      By continuing you accept our <a
        class="font-semibold underline"
        href="https://sweat.com/privacy-policy"
        target="_blank">Privacy Policy</a
      >
      and
      <a class="font-semibold underline" href="https://sweat.com/terms-of-use" target="_blank"
        >Terms of Use</a
      >.
    </Checkbox>
  </div>

  {#if form?.errors?.message && !emailTaken}
    <p class="text-black bg-error-100 rounded-sm my-4 p-4">
      {form.errors.message}
    </p>
  {/if}

  <input type="hidden" name="time_zone" value={Intl.DateTimeFormat().resolvedOptions().timeZone} />

  <Button
    dataTestId="create-account"
    text={loading ? 'Creating account...' : 'Create your account'}
    variant={BUTTON_VARIANT.PRIMARY}
    buttonSize={BUTTON_SIZE.LARGE}
    customClass="flex-1"
    plausibleClass="email_account"
    disabled={loading || !acceptTerms}
    onClick={() => {
      createAccount();
    }}
  />
</form>
